import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronDown, MapPin, Search } from 'lucide-react';
import { londonBoroughs, additionalAreas } from '../data/boroughs';
import { brandTheme } from '../styles/BrandTheme';

const SelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 2px solid ${brandTheme.colors.primary[600]};
  border-radius: 8px;
  color: ${brandTheme.colors.primary[600]};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;

  &:hover {
    background: ${brandTheme.colors.primary[50]};
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(254, 206, 27, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 180px;
    padding: 0.625rem 0.875rem;
  }
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    max-height: 300px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 1px solid ${brandTheme.colors.neutral.gray[200]};
  border-radius: 8px 8px 0 0;
  font-size: 0.9rem;
  outline: none;

  &::placeholder {
    color: ${brandTheme.colors.neutral.gray[400]};
  }
`;

const BoroughList = styled.div`
  max-height: 320px;
  overflow-y: auto;

  @media (max-width: 768px) {
    max-height: 220px;
  }
`;

const BoroughItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: ${brandTheme.colors.neutral.gray[700]};
  text-decoration: none;
  transition: all 0.2s ease;
  border-bottom: 1px solid ${brandTheme.colors.neutral.gray[100]};

  &:hover {
    background: ${brandTheme.colors.primary[50]};
    color: ${brandTheme.colors.primary[600]};
  }

  &:last-child {
    border-bottom: none;
  }

  .icon {
    color: ${brandTheme.colors.primary[600]};
    flex-shrink: 0;
  }

  .borough-info {
    flex: 1;
    
    .name {
      font-weight: 600;
      font-size: 0.9rem;
      margin-bottom: 0.125rem;
    }
    
    .areas {
      font-size: 0.75rem;
      color: ${brandTheme.colors.neutral.gray[500]};
    }
  }

  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    
    .borough-info {
      .name {
        font-size: 0.85rem;
      }
      
      .areas {
        font-size: 0.7rem;
      }
    }
  }
`;

const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${brandTheme.colors.neutral.gray[500]};
  font-size: 0.9rem;
`;

const BoroughSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBorough, setSelectedBorough] = useState<string>('Select Borough');

  const allAreas = [...londonBoroughs, ...additionalAreas];
  const filteredBoroughs = allAreas.filter(borough =>
    borough.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    borough.keyAreas.some(area => area.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleBoroughSelect = (boroughName: string, boroughSlug: string) => {
    setSelectedBorough(boroughName);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClickOutside = (e: React.MouseEvent) => {
    if ((e.target as Element).closest('.borough-selector')) {
      return;
    }
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside as any);
      return () => document.removeEventListener('click', handleClickOutside as any);
    }
  }, [isOpen]);

  return (
    <SelectorContainer className="borough-selector">
      <SelectorButton onClick={() => setIsOpen(!isOpen)}>
        <MapPin size={16} />
        <span>{selectedBorough}</span>
        <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }} />
      </SelectorButton>

      <Dropdown $isOpen={isOpen}>
        <SearchInput
          type="text"
          placeholder="Search areas or counties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
        <BoroughList>
          {filteredBoroughs.length > 0 ? (
            filteredBoroughs.map((borough) => (
              <BoroughItem
                key={borough.slug}
                to={`/resin-driveways-${borough.slug}/`}
                onClick={() => handleBoroughSelect(borough.name, borough.slug)}
              >
                <MapPin size={14} className="icon" />
                <div className="borough-info">
                  <div className="name">{borough.name}</div>
                  <div className="areas">{borough.keyAreas.slice(0, 3).join(', ')}</div>
                </div>
              </BoroughItem>
            ))
          ) : (
            <NoResults>No areas found matching "{searchTerm}"</NoResults>
          )}
        </BoroughList>
      </Dropdown>
    </SelectorContainer>
  );
};

export default BoroughSelector;
